import * as notificationGraphql from '../../graphql/notifications'
import CategoryForm from './CategoryForm'
import { DeliveryList } from './DeliveryList'
import { ProviderPanel } from './ProviderPanel'
import { ResourcePanel } from './ResourcePanel'
import SubscriptionForm from './SubscriptionForm'
import TemplateForm from './TemplateForm'
import {
  NotificationCapabilities,
  NotificationTabKey,
  bindNotificationScope
} from './notificationAccess'
import {
  CATEGORY_COLUMNS,
  CHANNEL_HEALTH_COLUMNS,
  RULE_COLUMNS,
  SUBSCRIPTION_COLUMNS,
  TEMPLATE_COLUMNS
} from './resourceColumns'
import { ErrorState, idempotencyKey } from './shared'

export function AdminPanel({
  tab,
  capabilities,
  currentScope
}: {
  tab: NotificationTabKey
  capabilities: NotificationCapabilities
  currentScope?: { type: string; code: string }
}) {
  if (!capabilities.globalScope && !currentScope) {
    return <ErrorState message="Unable to resolve your notification scope." />
  }
  switch (tab) {
    case 'health':
      return (
        <ResourcePanel
          title="Channel health"
          columns={CHANNEL_HEALTH_COLUMNS}
          query={notificationGraphql.NOTIFICATION_CHANNEL_HEALTH_QUERY}
          resultKey="notificationChannelHealth"
          listKey="health"
          paginated={false}
          queryVariables={
            !capabilities.globalScope && currentScope
              ? { scope: currentScope }
              : {}
          }
          canManage={false}
        />
      )
    case 'deliveries':
      return (
        <DeliveryList
          heading="Deliveries"
          admin
          canManage={capabilities.adminManage}
          queryVariables={
            !capabilities.globalScope && currentScope
              ? { entity: currentScope }
              : {}
          }
        />
      )
    case 'templates':
      if (!capabilities.globalScope) {
        return <ErrorState message="Global notification scope is required." />
      }
      return (
        <ResourcePanel
          title="Templates"
          columns={TEMPLATE_COLUMNS}
          renderEditor={(submit, saving, editing) => (
            <TemplateForm
              initial={editing}
              saving={saving}
              onSubmit={(value) => submit(value)}
            />
          )}
          query={notificationGraphql.NOTIFICATION_TEMPLATES_QUERY}
          resultKey="notificationTemplates"
          listKey="templates"
          canManage={capabilities.adminManage}
          mutation={notificationGraphql.UPSERT_NOTIFICATION_TEMPLATE_MUTATION}
          variableName="template"
          actionMutation={
            notificationGraphql.PREVIEW_NOTIFICATION_TEMPLATE_MUTATION
          }
          actionLabel="Preview"
          actionVariables={(row) => ({ id: row.id, variables: {} })}
        />
      )
    case 'categories':
      if (!capabilities.globalScope) {
        return <ErrorState message="Global notification scope is required." />
      }
      return (
        <ResourcePanel
          title="Categories"
          columns={CATEGORY_COLUMNS}
          renderEditor={(submit, saving, editing) => (
            <CategoryForm
              initial={editing}
              saving={saving}
              onSubmit={(value) => submit(value)}
            />
          )}
          query={notificationGraphql.NOTIFICATION_ADMIN_CATEGORIES_QUERY}
          resultKey="notificationAdminCategories"
          listKey="categories"
          canManage={capabilities.adminManage}
          mutation={notificationGraphql.UPSERT_NOTIFICATION_CATEGORY_MUTATION}
          variableName="category"
        />
      )
    case 'rules':
      if (!capabilities.globalScope) {
        return <ErrorState message="Global notification scope is required." />
      }
      return (
        <ResourcePanel
          title="Rules"
          columns={RULE_COLUMNS}
          query={notificationGraphql.NOTIFICATION_ADMIN_RULES_QUERY}
          resultKey="notificationAdminRules"
          listKey="rules"
          canManage={capabilities.adminManage}
          mutation={notificationGraphql.UPSERT_NOTIFICATION_RULE_MUTATION}
          variableName="rule"
          deleteMutation={notificationGraphql.DELETE_NOTIFICATION_RULE_MUTATION}
          actionMutation={
            notificationGraphql.SIMULATE_NOTIFICATION_RULE_MUTATION
          }
          actionLabel="Simulate"
          actionVariables={(row) => ({ id: row.id, event: {} })}
        />
      )
    case 'subscriptions':
      return (
        <ResourcePanel
          title="Subscriptions"
          columns={SUBSCRIPTION_COLUMNS}
          renderEditor={
            capabilities.globalScope
              ? (submit, saving, editing) => (
                  <SubscriptionForm
                    initial={editing}
                    saving={saving}
                    scopeBound={false}
                    onSubmit={(value) => submit(value)}
                  />
                )
              : undefined
          }
          query={notificationGraphql.NOTIFICATION_ADMIN_SUBSCRIPTIONS_QUERY}
          resultKey="notificationAdminSubscriptions"
          listKey="subscriptions"
          queryVariables={
            !capabilities.globalScope && currentScope
              ? { entity: currentScope }
              : {}
          }
          canManage={capabilities.adminManage && capabilities.globalScope}
          mutation={
            notificationGraphql.UPSERT_NOTIFICATION_SUBSCRIPTION_MUTATION
          }
          variableName="subscription"
          mutationVariables={(value) => ({
            subscription:
              !capabilities.globalScope && currentScope
                ? bindNotificationScope(value, currentScope, 'entity')
                : value,
            idempotencyKey: idempotencyKey()
          })}
          deleteMutation={
            notificationGraphql.DELETE_NOTIFICATION_SUBSCRIPTION_MUTATION
          }
        />
      )
    case 'providers':
      return (
        <ProviderPanel
          canManage={capabilities.providerManage}
          globalScope={capabilities.globalScope}
          currentScope={currentScope}
        />
      )
    default:
      return null
  }
}
